import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './auth.css';
import { API_BASE } from '../config/api';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifiedMsg, setVerifiedMsg] = useState('');

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const verified = sp.get('verified');
    const message = sp.get('message');
    
    if (verified === '1') {
      setVerifiedMsg('Your email has been verified. You can now log in.');
    } else if (verified === '0') {
      setVerifiedMsg('Verification link is invalid or expired. Please try again.');
    } else if (message === 'payment-success') {
      setVerifiedMsg('Payment completed successfully! Your subscription is now active. You can log in.');
    } else if (message === 'payment-cancelled') {
      setVerifiedMsg('Payment was cancelled. You can try again or contact support if you need help.');
    } else if (message === 'payment-error') {
      setVerifiedMsg('There was an error processing your payment. Please try again or contact support.');
    }
    
    if (verified || message) {
      // Clean the query string
      const url = window.location.pathname;
      window.history.replaceState({}, '', url);
    }
  }, []);

  const startOAuth = (provider) => {
    const qs = new URLSearchParams({ mode: 'login' });
    window.location.href = `${API_BASE}/auth/oauth/${provider}/start?${qs.toString()}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Handle payment required responses
        if (res.status === 402) {
          if (data.paymentRequired) {
            setError('Your account is pending payment. Please complete your subscription payment to activate your account.');
            setVerifiedMsg('Payment required: You must complete your Stripe payment before you can log in. Check your email for the payment link or contact support.');
            return;
          } else if (data.subscriptionRequired) {
            setError('No active subscription found. Please select a plan and complete payment.');
            setTimeout(() => {
              window.location.href = '/signup';
            }, 2000);
            return;
          }
        }
        throw new Error(data.error || 'Login failed');
      }

      // Save token and user
      if (data.token) localStorage.setItem('token', data.token);
      if (data.user) localStorage.setItem('user', JSON.stringify(data.user));

      // Compute target route based on backend redirect or user role
      const mapRedirect = (path) => {
        switch (path) {
          case '/super/dashboard':
            return '/dashboard/super-admin';
          case '/admin/dashboard':
            return '/dashboard/admin';
          case '/viewer/home':
            return '/dashboard/viewer';
          default:
            return null;
        }
      };

      let target = mapRedirect(data.redirect);
      if (!target) {
        const roleFromUser = (data.user?.globalRole || data.user?.role || '')
          .toString()
          .toLowerCase();
        const roles = (data.user?.roles || []).map((r) => String(r).toLowerCase());
        const all = [roleFromUser, ...roles];
        if (all.some((r) => r === 'super admin' || r === 'super_admin' || r.includes('super'))) {
          target = '/dashboard/super-admin';
        } else if (all.some((r) => r === 'admin' || r.includes('owner') || r.includes('business'))) {
          target = '/dashboard/admin';
        } else {
          target = '/dashboard/viewer';
        }
      }

      navigate(target, { replace: true });
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <h1>Log in</h1>
        {verifiedMsg && (
          <div className={`auth-${verifiedMsg.includes('verified') ? 'success' : 'error'}`} role="status">{verifiedMsg}</div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="auth-field">
            <label>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error && (
            <div className="auth-error" role="alert">{error}</div>
          )}
          <div className="auth-actions">
            <button className="button" type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Log in'}
            </button>
            <a href="/auth/reset">Forgot password?</a>
          </div>
          <div className="social-buttons">
            <button type="button" className="social-btn google" onClick={() => startOAuth('google')}>
              <span className="icon" aria-hidden="true">
                <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12  s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C33.266,6.053,28.862,4,24,4C12.954,4,4,12.954,4,24  s8.954,20,20,20s20-8.954,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                  <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,16.108,18.961,13,24,13c3.059,0,5.842,1.154,7.961,3.039  l5.657-5.657C33.266,6.053,28.862,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                  <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.195l-6.197-5.238C29.173,35.091,26.715,36,24,36  c-5.202,0-9.619-3.317-11.283-7.946l-6.524,5.025C9.505,39.556,16.227,44,24,44z"/>
                  <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.793,2.238-2.231,4.166-4.093,5.567  c0.001-0.001,0.002-0.001,0.003-0.002l6.197,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                </svg>
              </span>
              <span>Continue with Google</span>
            </button>
            <button type="button" className="social-btn microsoft" onClick={() => startOAuth('microsoft')}>
              <span className="icon" aria-hidden="true">
                <svg viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                  <rect width="10" height="10" x="0" y="0" fill="#f25022"/>
                  <rect width="10" height="10" x="12.5" y="0" fill="#7fba00"/>
                  <rect width="10" height="10" x="0" y="12.5" fill="#00a4ef"/>
                  <rect width="10" height="10" x="12.5" y="12.5" fill="#ffb900"/>
                </svg>
              </span>
              <span>Continue with Microsoft</span>
            </button>
          </div>
        </form>
        <div className="help-links">
          <span>New here? <a href="/signup">Create an account</a></span>
        </div>
      </div>
    </div>
  );
};

export default Login;
